"use client";

import { useEffect, useRef, useState } from "react";
import { capturePostHogEvent, onPostHogReady } from "@/lib/posthog";
import { PdpGallery, type PdpGalleryImage } from "@/components/PdpGallery";

export const PDP_GALLERY_FLAG = "pdp-gallery-info-v1";
export const PDP_GALLERY_EXPOSURE = "pdp_gallery_exposed";
type Variant = "control" | "info";

/** Treatment leads with editorial images. The arm resolves in the browser: the landing HTML is
 * shared through the edge cache, so the server must not render one visitor's assignment into it.
 * Every visitor first sees the control gallery; a late "info" result replaces it only while nobody
 * has touched it (at 0% today, so nobody is swapped). Only a resolved, visible gallery records
 * exposure, with the arm actually rendered. */
export function PdpGalleryExperiment({ control, treatment, thumbnailsLabel }: {
  control: PdpGalleryImage[];
  treatment: PdpGalleryImage[];
  thumbnailsLabel: string;
}) {
  const [variant, setVariant] = useState<Variant | null>(null);
  const [visible, setVisible] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const interacted = useRef(false);
  const exposed = useRef(false);
  const preview = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      const override = new URLSearchParams(window.location.search).get("pdpGallery");
      if (override === "control" || override === "info") {
        preview.current = true;
        setVariant(override);
        return;
      }
    }
    // posthog-js loads when DeferredThirdParty fires (first interaction, or 6 s after load), not
    // here: initialising it at hydration put ~61 KB (brotli) of script inside the LCP window of
    // /en. Until then everyone sees the control gallery; interaction freezes the shown gallery.
    let disposed = false;
    let resolved = false;
    let unsubscribe: (() => void) | undefined;
    onPostHogReady((posthog) => {
      if (disposed) return;
      unsubscribe = posthog.onFeatureFlags(() => {
        if (disposed || resolved || interacted.current) return;
        const arm = posthog.getFeatureFlag(PDP_GALLERY_FLAG, { send_event: false });
        if (arm !== "control" && arm !== "info") return;
        resolved = true;
        setVariant(arm);
      });
    });
    return () => { disposed = true; unsubscribe?.(); };
  }, []);

  useEffect(() => {
    if (!root.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, { threshold: 0.5 });
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!variant || !visible || exposed.current || preview.current) return;
    exposed.current = true;
    capturePostHogEvent(PDP_GALLERY_EXPOSURE, {
      [`$feature/${PDP_GALLERY_FLAG}`]: variant,
      experiment_key: PDP_GALLERY_FLAG,
      variant,
      gallery_version: 1,
      vehicle: "car",
    });
  }, [variant, visible]);

  return (
    <div ref={root} data-gallery-variant={variant ?? "unassigned"}
      onPointerDownCapture={() => { interacted.current = true; }}
      onKeyDownCapture={() => { interacted.current = true; }}>
      <PdpGallery images={variant === "info" ? treatment : control} thumbnailsLabel={thumbnailsLabel} />
    </div>
  );
}
